import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import usePageMeta from '@/hooks/usePageMeta';
import useJsonLd, { articleJsonLd } from '@/lib/jsonLd';
import { mainSiteUrl } from '@/mocks/categories';
import { pillars } from '@/mocks/pillars';

const keywordsBySlug: Record<string, string> = {
  review: 'AllChinaBuy review, AllChinaBuy reviews, is AllChinaBuy legit',
  shipping: 'AllChinaBuy shipping cost, AllChinaBuy shipping guide, AllChinaBuy shipping time',
  coupon: 'AllChinaBuy coupon, AllChinaBuy discount code, AllChinaBuy promo code',
  warehouse: 'AllChinaBuy warehouse, AllChinaBuy warehouse QC, AllChinaBuy warehouse shipping',
  legit: 'is AllChinaBuy legit, is AllChinaBuy safe, AllChinaBuy review',
  qc: 'AllChinaBuy QC, AllChinaBuy QC photos, AllChinaBuy quality check',
  beginner: 'AllChinaBuy beginner guide, how to use AllChinaBuy, AllChinaBuy buying guide',
  comparison: 'AllChinaBuy vs Superbuy, AllChinaBuy alternatives, AllChinaBuy comparison',
  research: 'AllChinaBuy product research, AllChinaBuy finds, how to find products on AllChinaBuy',
  sellers: 'AllChinaBuy trusted sellers, AllChinaBuy seller guide, AllChinaBuy seller reviews',
};

export default function PillarPage() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const pillar = pillars.find((p) => p.slug === slug);
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  const pillarTitle = pillar ? t(`pillar.${slug}.title`) : 'Guide Not Found';
  const pillarDescription = pillar ? t(`pillar.${slug}.excerpt`) : undefined;
  const pillarPath = pillar ? `/guide/${slug}` : undefined;

  usePageMeta({
    title: pillarTitle,
    description: pillarDescription,
    keywords: pillar ? keywordsBySlug[slug as string] : undefined,
    path: pillarPath,
    type: 'article',
    image: pillar?.image,
    noindex: !pillar,
  });

  useJsonLd(
    pillar
      ? articleJsonLd({
          path: pillarPath as string,
          title: pillarTitle,
          description: pillarDescription,
          image: pillar.image,
        })
      : null,
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [slug]);

  if (!pillar) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="flex min-h-[60vh] items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950">Guide not found</h1>
            <Link
              to="/"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary-700 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              {t('article.backHome')}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const sections = [
    { title: t(`pillar.${slug}.s1.title`), body: t(`pillar.${slug}.s1.body`) },
    { title: t(`pillar.${slug}.s2.title`), body: t(`pillar.${slug}.s2.body`) },
    { title: t(`pillar.${slug}.s3.title`), body: t(`pillar.${slug}.s3.body`) },
  ];
  const takeaways = ['t1', 't2', 't3'].map((k) => t(`pillar.${slug}.${k}`));
  const faqs = [
    { q: t(`pillar.${slug}.f1.q`), a: t(`pillar.${slug}.f1.a`) },
    { q: t(`pillar.${slug}.f2.q`), a: t(`pillar.${slug}.f2.a`) },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-16">
        <article className="mx-auto w-full max-w-3xl px-4 py-10 md:px-6 md:py-16">
          <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700 cursor-pointer">
              {t('nav.brand')}
            </Link>
            <span>/</span>
            <span>{t('nav.shoppingGuide')}</span>
            <span>/</span>
            <span className="text-foreground-400">{t(`pillar.${slug}.title`)}</span>
          </nav>

          <header className="mt-6">
            <span className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary-700">
              <i className={`${pillar.icon}`}></i>
              {t('nav.shoppingGuide')}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {t(`pillar.${slug}.title`)}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {t('article.author')}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {pillar.readTime}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-check-line"></i>
                {t('article.updated')}
              </span>
            </div>
          </header>

          <div className="mt-8 overflow-hidden rounded-2xl">
            <img
              src={pillar.image}
              alt={t(`pillar.${slug}.title`)}
              className="h-56 w-full object-cover md:h-72"
            />
          </div>

          <div className="mt-8 rounded-xl border border-primary-200 bg-primary-50 p-5">
            <p className="flex items-center gap-2 text-sm font-bold text-primary-800">
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-flashlight-line"></i>
              </span>
              {t('article.quickAnswer.label')}
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground-700">
              {t(`pillar.${slug}.quickAnswer`)}
            </p>
          </div>

          {sections.map((section) => (
            <section key={section.title} className="mt-10">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">
                {section.title}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">{section.body}</p>
            </section>
          ))}

          <div className="mt-10 rounded-xl border border-background-200/70 bg-background-100 p-6">
            <p className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
              <span className="flex h-5 w-5 items-center justify-center text-primary-700">
                <i className="ri-list-check-3"></i>
              </span>
              {t('pillar.takeaways')}
            </p>
            <ul className="mt-4 space-y-3">
              {takeaways.map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-primary-700">
                    <i className="ri-check-double-line"></i>
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10 rounded-xl border border-background-200/70 bg-background-100 p-6">
            <p className="flex items-center gap-2 font-heading text-base font-bold text-foreground-900">
              <span className="flex h-5 w-5 items-center justify-center text-primary-700">
                <i className="ri-links-line"></i>
              </span>
              {t('article.related.title')}
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800 cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                {t('article.related.home')}
              </Link>
              <button
                type="button"
                onClick={() => {
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 transition-colors hover:text-primary-800 cursor-pointer"
              >
                <i className="ri-arrow-up-line"></i>
                {t('article.backHome')}
              </button>
            </div>
          </div>
        </article>

        <section className="bg-background-100">
          <div className="mx-auto w-full max-w-3xl px-4 py-12 md:px-6 md:py-16">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              {t('article.faq.title')}
            </h2>
            <div className="mt-8 space-y-3">
              {faqs.map((item, index) => {
                const isOpen = openFaq === index;
                return (
                  <div
                    key={item.q}
                    className="overflow-hidden rounded-xl border border-background-200/70 bg-white"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenFaq(isOpen ? null : index)}
                      className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer"
                    >
                      <span className="font-heading text-sm font-semibold text-foreground-900 md:text-base">
                        {item.q}
                      </span>
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-background-300 text-foreground-500">
                        <i
                          className={`ri-add-line text-sm transition-transform ${isOpen ? 'rotate-45' : ''}`}
                        ></i>
                      </span>
                    </button>
                    {isOpen && (
                      <p className="px-6 pb-5 text-sm leading-relaxed text-foreground-600">{item.a}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="bg-primary-500">
          <div className="mx-auto w-full max-w-4xl px-4 py-14 text-center md:px-6 md:py-16">
            <h2 className="font-heading text-2xl font-bold text-white md:text-3xl">
              {t('article.cta.title')}
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/80 md:text-base">
              {t('article.cta.subtitle')}
            </p>
            <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-background-100 sm:w-auto cursor-pointer"
              >
                {t('article.cta.button1')}
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-up-line"></i>
                </span>
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-full border border-white/40 px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10 sm:w-auto cursor-pointer"
              >
                {t('article.cta.button2')}
              </a>
            </div>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white/70 underline-offset-4 hover:underline cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              {t('article.backHome')}
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}